import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Film } from "lucide-react";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import { ytThumb, ytThumbHd, ytEmbed } from "@/lib/youtube";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeaturedPlayer = ({ video }) => {
    const [playing, setPlaying] = useState(false);

    return (
        <div
            data-testid="featured-video-player"
            className="relative rounded-2xl overflow-hidden gold-ring border border-amber-500/25 bg-black"
        >
            <AnimatePresence mode="wait">
                {playing ? (
                    <motion.div key="iframe" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="aspect-video">
                        <iframe
                            src={ytEmbed(video.youtube_id)}
                            title={video.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                            data-testid="featured-video-iframe"
                        />
                    </motion.div>
                ) : (
                    <motion.button
                        key="poster"
                        exit={{ opacity: 0 }}
                        onClick={() => setPlaying(true)}
                        data-testid={`video-play-button-${video.id}`}
                        className="group relative block w-full aspect-video text-left"
                    >
                        <img
                            src={ytThumbHd(video.youtube_id)}
                            onError={(e) => {
                                e.currentTarget.src = ytThumb(video.youtube_id);
                            }}
                            alt={video.title}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className="w-20 h-20 rounded-full bg-amber-500 text-black flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-[0_0_40px_rgba(200,151,43,0.55)]">
                                <Play size={30} fill="currentColor" className="ml-1" />
                            </span>
                        </div>
                        <div className="absolute bottom-0 inset-x-0 p-6 sm:p-8">
                            {video.caption && (
                                <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-amber-400">{video.caption}</p>
                            )}
                            <h3 className="font-display text-xl sm:text-2xl font-bold text-white mt-2">{video.title}</h3>
                        </div>
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    );
};

const VideoCard = ({ video, active, onSelect }) => (
    <button
        onClick={onSelect}
        data-testid={`video-card-${video.id}`}
        className={`group relative text-left rounded-xl overflow-hidden border transition-all duration-300 w-full ${
            active
                ? "border-amber-400/70 shadow-[0_0_25px_rgba(200,151,43,0.2)]"
                : "border-amber-500/15 hover:border-amber-400/40"
        }`}
    >
        <div className="relative aspect-video overflow-hidden">
            <img
                src={ytThumb(video.youtube_id)}
                alt={video.title}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-black/35 group-hover:bg-black/15 transition-colors duration-300" />
            <span className="absolute inset-0 m-auto w-11 h-11 rounded-full bg-black/60 backdrop-blur border border-amber-500/40 text-amber-300 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-black transition-all duration-300">
                <Play size={16} fill="currentColor" />
            </span>
        </div>
        <div className="p-4 bg-zinc-900/70">
            {video.caption && (
                <p className="font-meta text-[9px] tracking-[0.3em] uppercase text-amber-400">{video.caption}</p>
            )}
            <p className="font-display text-sm font-semibold text-white mt-1.5 leading-snug">{video.title}</p>
        </div>
    </button>
);

const EmptyState = () => (
    <FadeIn>
        <div
            data-testid="videos-empty-state"
            className="rounded-2xl border border-dashed border-amber-500/25 bg-zinc-900/40 px-6 py-20 text-center"
        >
            <Film size={30} className="mx-auto text-amber-500/60" />
            <p className="font-display text-xl font-bold text-white mt-5">New videos coming soon</p>
            <p className="text-sm text-zinc-400 mt-2 max-w-sm mx-auto leading-relaxed">
                Official visuals and live moments are on the way. Follow along so you catch the first drop.
            </p>
        </div>
    </FadeIn>
);

const Videos = () => {
    const [videos, setVideos] = useState(null);
    const [featuredId, setFeaturedId] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/videos`)
            .then((res) => {
                setVideos(res.data);
                if (res.data.length > 0) setFeaturedId(res.data[0].id);
            })
            .catch(() => setVideos([]));
    }, []);

    const featured = (videos || []).find((v) => v.id === featuredId);

    return (
        <div data-testid="videos-page" className="pt-16 sm:pt-[72px]">
            <section className="relative px-5 sm:px-8 pt-20 pb-12 overflow-hidden">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[80vw] h-[50vh] bg-amber-500/10 blur-[130px] rounded-full pointer-events-none" />
                <div className="max-w-7xl mx-auto">
                    <SectionHeading eyebrow="Visuals" title="Music Videos & Live Moments" />
                    <FadeIn delay={0.1}>
                        <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">
                            Official videos, live performances, and behind-the-scenes sessions.
                        </p>
                    </FadeIn>
                </div>
            </section>

            <section className="px-5 sm:px-8 pb-24">
                <div className="max-w-7xl mx-auto">
                    {videos === null ? (
                        <div className="grid lg:grid-cols-3 gap-6">
                            <div className="lg:col-span-2 rounded-2xl border border-amber-500/10 bg-zinc-900/40 aspect-video animate-pulse" />
                            <div className="grid gap-5 content-start">
                                {[...Array(2)].map((_, i) => (
                                    <div key={i} className="rounded-xl border border-amber-500/10 bg-zinc-900/40 aspect-video animate-pulse" />
                                ))}
                            </div>
                        </div>
                    ) : videos.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <div className="grid lg:grid-cols-3 gap-6">
                            <FadeIn className="lg:col-span-2">
                                {featured && <FeaturedPlayer key={featured.id} video={featured} />}
                            </FadeIn>
                            <div className="grid gap-5 content-start">
                                {videos.map((v, i) => (
                                    <FadeIn key={v.id} delay={i * 0.08}>
                                        <VideoCard
                                            video={v}
                                            active={featuredId === v.id}
                                            onSelect={() => {
                                                setFeaturedId(v.id);
                                                if (window.__lenis) window.__lenis.scrollTo(0);
                                                else window.scrollTo({ top: 0, behavior: "smooth" });
                                            }}
                                        />
                                    </FadeIn>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Videos;
